import { Button, Group, Modal, Stack, Title } from '@mantine/core'
import { PaymentParams } from 'app/models/payments-params'
import {
  submitPayments,
  releasePayments,
  settlePayments,
  cancelPayment,
  queuePayments,
  loadActivePayments
} from 'app/state/ducks/payments/thunks'
import { formatNumberToCurrency } from 'app/utils/util-functions'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getPaymentMessage } from './payment-utils'
import { submitPayment } from 'app/state/ducks/payments/actions'

interface Props {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  payment: PaymentParams
  lenderAllocations?: any
  isCanceled?: boolean
}

export default function PaymentModal({
  open,
  setOpen,
  payment,
  isCanceled,
}: Props) {
  const dispatch = useDispatch()
  const [action, setAction] = useState<string>('')

  useEffect(() => {
    setAction(isCanceled
      ? 'Cancel'
      : payment.status == 'Rejected'
        ? 'Queue'
        : payment.status == 'Released'
          ? 'Settle'
          : payment.submitted
            ? 'Release'
            : 'Submit for Release')
  }, [isCanceled, payment])

  const handleProceedClick = async (payment: any) => {
    let response: any = null
    setOpen(false)
    if (isCanceled) {
      response = await dispatch(cancelPayment(payment))
    }
    if (!isCanceled && payment.status == 'Rejected') {
      response = await dispatch(queuePayments(payment))
    }

    if (!isCanceled && payment.status == 'Released') {
      response = await dispatch(settlePayments(payment))
    }
    if (!isCanceled && payment.status == 'Queued' && payment.submitted) {
      response = await dispatch(releasePayments(payment))
    }
    if (!isCanceled && payment.status == 'Queued' && !payment.submitted) {
      response = await dispatch(submitPayments(payment))
    }
    if (!response) {
      return
    }

    const { title, message } = getPaymentMessage(
      isCanceled,
      payment,
      response.success
    )

    if (response.success) {
      SuccessNotification({ title, message })
      if (!isCanceled && payment.status == 'Queued' && !payment.submitted) {
        // The grid is one PQS read (ADR_LOS-030). PQS can be a moment behind
        // the ledger, so the re-read may still say this payment is
        // unsubmitted, and it replaces the whole list — which would undo the
        // submit the thunk just applied and offer "Submit for Release" again
        // (a second click fails on the ledger). Re-apply it after the re-read.
        await dispatch(loadActivePayments())
        dispatch(submitPayment({ id: payment.id }))
      }

    } else {
      ErrorNotification({ title, message: response.payload ?? message })
    }
  }

  return (
    <Modal opened={open} onClose={() => setOpen(false)} size="lg">
      <Stack>
        <Title>{action} {formatNumberToCurrency(Number(payment.payment.amount), payment.payment.currency ?? 'USD')} Payment </Title>
        <Group position="right">
          <Button onClick={() => setOpen(false)}>Cancel</Button>

          <Button
            style={{ backgroundColor: '#32439A' }}
            onClick={() => handleProceedClick(payment)}
          >
            Proceed
          </Button>

        </Group>
      </Stack>
    </Modal>
  )
}
